// src/utils/numberFormat.js

// Format a number with consistent decimal places
const formatDecimal = (value, decimals = 2) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
  const num = Number(value);
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  });
};

// Format currency with consistent decimal places
export const formatCurrency = (value, currency = 'USD', decimals = 2) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
  const num = Number(value);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  }).format(num);
};

// Format percentage with consistent decimal places
export const formatPercentage = (value, decimals = 2) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
  const num = Number(value);
  return num.toLocaleString('en-US', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  });
};

// Format crypto amounts with appropriate decimal places
export const formatCrypto = (value, symbol = '', decimals = 8) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
  const num = Number(value);
  
  // For very small numbers, show up to 8 decimal places
  if (num < 0.0001) {
    return `${num.toFixed(8)} ${symbol}`.trim();
  }
  
  // For numbers less than 1, show 4-6 decimal places
  if (num < 1) {
    return `${num.toFixed(6)} ${symbol}`.trim();
  }
  
  // For larger numbers, show 2-4 decimal places
  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4
  });
  
  return `${formatted} ${symbol}`.trim();
};

// Format large numbers with K, M, B, T suffixes
export const formatCompactNumber = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
  const num = Number(value);
  
  // For numbers less than 1 million, use regular formatting
  if (Math.abs(num) < 1000000) {
    return num.toLocaleString('en-US');
  }
  
  // For larger numbers, use compact notation
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 2
  }).format(num);
};

// Alias for backward compatibility
export const formatDisplayNumber = formatCompactNumber;

// Format price with appropriate decimal places
export const formatPrice = (value, currency = 'USD') => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
  const num = Number(value);
  
  if (currency === 'USD') {
    if (num >= 1) {
      return `$${num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    } else {
      return `$${num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6
      })}`;
    }
  }
  
  return `${num.toLocaleString('en-US')} ${currency}`.trim();
};

// Format percentage change with sign and color
export const formatChange = (value, decimals = 2) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
  const num = Number(value);
  const isPositive = num >= 0;
  const sign = isPositive ? '+' : '';
  const formatted = Math.abs(num).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  });
  
  return {
    value: `${sign}${formatted}%`,
    isPositive
  };
};
